import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { LayoutGrid, BarChart3, Package, FileText, Users, LogOut, Plus, Pencil, Trash2, Box, TrendingUp, Settings, Save } from 'lucide-react';
import { toast } from 'sonner';
import AnalyticsDashboard from '@/components/AnalyticsDashboard';
import AppHeader from '@/components/AppHeader';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function AdminDashboard() {
  const { logout } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState({});
  const [products, setProducts] = useState([]);
  const [quotes, setQuotes] = useState([]);
  const [orders, setOrders] = useState([]);
  const [inventory, setInventory] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [productDialogOpen, setProductDialogOpen] = useState(false);
  const [inventoryDialogOpen, setInventoryDialogOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [productForm, setProductForm] = useState({
    name: '', category: 'window', style: '', material: '', base_price: 0, price_per_sqft: 0, description: ''
  });
  const [inventoryForm, setInventoryForm] = useState({
    product_id: '', quantity: 0, location: '', notes: ''
  });
  const [terms, setTerms] = useState('');
  const [termsSaving, setTermsSaving] = useState(false);

  useEffect(() => {
    fetchAll();
  }, []);

  const fetchAll = () => {
    fetchStats(); fetchProducts(); fetchQuotes(); fetchOrders(); fetchInventory(); fetchCustomers(); fetchTerms();
  };

  const fetchStats = async () => {
    try { const { data } = await axios.get(`${BACKEND_URL}/api/dashboard/stats`, { withCredentials: true }); setStats(data); } catch (e) { console.error(e); }
  };
  const fetchProducts = async () => {
    try { const { data } = await axios.get(`${BACKEND_URL}/api/products`, { withCredentials: true }); setProducts(data); } catch (e) { console.error(e); }
  };
  const fetchQuotes = async () => {
    try { const { data } = await axios.get(`${BACKEND_URL}/api/quotes`, { withCredentials: true }); setQuotes(data); } catch (e) { console.error(e); }
  };
  const fetchOrders = async () => {
    try { const { data } = await axios.get(`${BACKEND_URL}/api/orders`, { withCredentials: true }); setOrders(data); } catch (e) { console.error(e); }
  };
  const fetchInventory = async () => {
    try { const { data } = await axios.get(`${BACKEND_URL}/api/inventory`, { withCredentials: true }); setInventory(data); } catch (e) { console.error(e); }
  };
  const fetchCustomers = async () => {
    try { const { data } = await axios.get(`${BACKEND_URL}/api/customers`, { withCredentials: true }); setCustomers(data); } catch (e) { console.error(e); }
  };
  const fetchTerms = async () => {
    try { const { data } = await axios.get(`${BACKEND_URL}/api/settings/terms`, { withCredentials: true }); setTerms(data.terms); } catch (e) { console.error(e); }
  };
  const handleSaveTerms = async () => {
    setTermsSaving(true);
    try {
      await axios.put(`${BACKEND_URL}/api/settings/terms`, { terms }, { withCredentials: true });
      toast.success('Terms & Conditions updated');
    } catch (e) { toast.error('Failed to update'); }
    finally { setTermsSaving(false); }
  };

  const handleSaveProduct = async () => {
    try {
      if (editingProduct) {
        await axios.put(`${BACKEND_URL}/api/products/${editingProduct.id}`, productForm, { withCredentials: true });
        toast.success('Product updated');
      } else {
        await axios.post(`${BACKEND_URL}/api/products`, productForm, { withCredentials: true });
        toast.success('Product created');
      }
      setProductDialogOpen(false);
      setEditingProduct(null);
      setProductForm({ name: '', category: 'window', style: '', material: '', base_price: 0, price_per_sqft: 0, description: '' });
      fetchProducts();
    } catch (e) {
      toast.error('Failed to save product');
    }
  };

  const handleDeleteProduct = async (id) => {
    if (!window.confirm('Delete this product?')) return;
    try {
      await axios.delete(`${BACKEND_URL}/api/products/${id}`, { withCredentials: true });
      toast.success('Product deleted');
      fetchProducts();
    } catch (e) {
      toast.error('Failed to delete');
    }
  };

  const handleUpdateQuoteStatus = async (id, status) => {
    try {
      await axios.patch(`${BACKEND_URL}/api/quotes/${id}/status?status=${status}`, {}, { withCredentials: true });
      toast.success('Status updated');
      fetchQuotes();
      fetchStats();
    } catch (e) {
      toast.error('Failed to update');
    }
  };

  const handleUpdateOrderStatus = async (id, status) => {
    try {
      await axios.patch(`${BACKEND_URL}/api/orders/${id}/status?status=${status}`, {}, { withCredentials: true });
      toast.success('Status updated');
      fetchOrders();
    } catch (e) {
      toast.error('Failed to update');
    }
  };

  const handleCreateInventory = async () => {
    try {
      await axios.post(`${BACKEND_URL}/api/inventory`, inventoryForm, { withCredentials: true });
      toast.success('Inventory added');
      setInventoryDialogOpen(false);
      setInventoryForm({ product_id: '', quantity: 0, location: '', notes: '' });
      fetchInventory();
    } catch (e) {
      toast.error('Failed to add inventory');
    }
  };

  const getStatusBadge = (status) => {
    const v = {
      pending: 'bg-yellow-100 text-yellow-800', ordered: 'bg-blue-100 text-blue-800',
      confirmed: 'bg-green-100 text-green-800', completed: 'bg-slate-100 text-slate-800'
    };
    return <Badge className={`${v[status] || v.pending} rounded-sm`}>{status}</Badge>;
  };

  const handleLogout = async () => { await logout(); navigate('/login'); };

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      <AppHeader title="Hi Tech Industries Admin">
        <Button variant="ghost" size="sm" onClick={() => navigate('/contacts')} className="text-slate-600 hover:text-slate-900" data-testid="contacts-button">
          <Users size={18} className="mr-2" /> Contacts
        </Button>
        <Button variant="ghost" size="sm" onClick={() => navigate('/configurator')} className="text-slate-600 hover:text-slate-900" data-testid="configurator-button">
          <LayoutGrid size={18} className="mr-2" /> Configurator
        </Button>
      </AppHeader>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
        <div className="mb-6 sm:mb-8">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-medium tracking-tight text-slate-900 mb-1">Admin Dashboard</h2>
          <p className="text-sm sm:text-base text-slate-500">Manage products, orders, and inventory</p>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mb-6 sm:mb-8">
          <Card className="border border-slate-200 rounded-sm shadow-none" data-testid="stat-total-quotes">
            <CardHeader className="p-4 sm:p-6 pb-2 sm:pb-3">
              <CardDescription className="text-[10px] sm:text-xs uppercase tracking-[0.2em]">Quotes</CardDescription>
              <CardTitle className="text-2xl sm:text-3xl font-medium">{stats.total_quotes || 0}</CardTitle>
            </CardHeader>
          </Card>
          <Card className="border border-slate-200 rounded-sm shadow-none" data-testid="stat-total-orders">
            <CardHeader className="p-6 pb-3">
              <CardDescription className="text-xs uppercase tracking-[0.2em]">Total Orders</CardDescription>
              <CardTitle className="text-3xl font-medium">{stats.total_orders || 0}</CardTitle>
            </CardHeader>
          </Card>
          <Card className="border border-slate-200 rounded-sm shadow-none" data-testid="stat-total-customers">
            <CardHeader className="p-4 sm:p-6 pb-2 sm:pb-3">
              <CardDescription className="text-[10px] sm:text-xs uppercase tracking-[0.2em]">Customers</CardDescription>
              <CardTitle className="text-2xl sm:text-3xl font-medium">{stats.total_customers || 0}</CardTitle>
            </CardHeader>
          </Card>
          <Card className="border border-slate-200 rounded-sm shadow-none" data-testid="stat-total-revenue">
            <CardHeader className="p-4 sm:p-6 pb-2 sm:pb-3">
              <CardDescription className="text-[10px] sm:text-xs uppercase tracking-[0.2em]">Revenue</CardDescription>
              <CardTitle className="text-2xl sm:text-3xl font-medium text-[#0052FF]">&#8377;{(stats.total_revenue || 0).toFixed(0)}</CardTitle>
            </CardHeader>
          </Card>
        </div>

        {/* Tabs */}
        <Tabs defaultValue="products" className="space-y-4 sm:space-y-6">
          <div className="overflow-x-auto -mx-4 px-4 sm:mx-0 sm:px-0">
            <TabsList className="bg-white border border-slate-200 p-1 rounded-sm inline-flex min-w-max">
              <TabsTrigger value="products" className="data-[state=active]:bg-slate-100 rounded-sm text-xs sm:text-sm" data-testid="products-tab">
                <Box size={16} className="mr-1.5" /> Products
              </TabsTrigger>
              <TabsTrigger value="quotes" className="data-[state=active]:bg-slate-100 rounded-sm text-xs sm:text-sm" data-testid="quotes-tab">
                <FileText size={16} className="mr-1.5" /> Quotes
              </TabsTrigger>
              <TabsTrigger value="orders" className="data-[state=active]:bg-slate-100 rounded-sm text-xs sm:text-sm" data-testid="orders-tab">
                <Package size={16} className="mr-1.5" /> Orders
              </TabsTrigger>
              <TabsTrigger value="inventory" className="data-[state=active]:bg-slate-100 rounded-sm text-xs sm:text-sm" data-testid="inventory-tab">
                <BarChart3 size={16} className="mr-1.5" /> Inventory
              </TabsTrigger>
              <TabsTrigger value="customers" className="data-[state=active]:bg-slate-100 rounded-sm text-xs sm:text-sm" data-testid="customers-tab">
                <Users size={16} className="mr-1.5" /> Customers
              </TabsTrigger>
              <TabsTrigger value="analytics" className="data-[state=active]:bg-slate-100 rounded-sm text-xs sm:text-sm" data-testid="analytics-tab">
                <TrendingUp size={16} className="mr-1.5" /> Analytics
              </TabsTrigger>
              <TabsTrigger value="settings" className="data-[state=active]:bg-slate-100 rounded-sm text-xs sm:text-sm" data-testid="settings-tab">
                <Settings size={16} className="mr-1.5" /> Settings
              </TabsTrigger>
            </TabsList>
          </div>

          {/* Products Tab */}
          <TabsContent value="products" className="space-y-4">
            <div className="flex justify-between items-center">
              <h3 className="text-2xl font-medium text-slate-900">Products</h3>
              <Dialog open={productDialogOpen} onOpenChange={setProductDialogOpen}>
                <DialogTrigger asChild>
                  <Button onClick={() => { setEditingProduct(null); setProductForm({ name: '', category: 'window', style: '', material: '', base_price: 0, price_per_sqft: 0, description: '' }); }} className="bg-[#0052FF] hover:bg-[#0043D1] rounded-sm" data-testid="add-product-button">
                    <Plus size={18} className="mr-2" /> Add Product
                  </Button>
                </DialogTrigger>
                <DialogContent className="rounded-sm max-w-2xl" data-testid="product-dialog">
                  <DialogHeader>
                    <DialogTitle>{editingProduct ? 'Edit Product' : 'Add New Product'}</DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4 py-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label className="text-xs font-medium uppercase tracking-[0.2em] text-slate-700">Product Name</Label>
                        <Input value={productForm.name} onChange={(e) => setProductForm({ ...productForm, name: e.target.value })} className="mt-2 rounded-sm" data-testid="product-name-input" />
                      </div>
                      <div>
                        <Label className="text-xs font-medium uppercase tracking-[0.2em] text-slate-700">Category</Label>
                        <Select value={productForm.category} onValueChange={(v) => setProductForm({ ...productForm, category: v })}>
                          <SelectTrigger className="mt-2 rounded-sm" data-testid="product-category-select"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="window">Window</SelectItem>
                            <SelectItem value="door">Door</SelectItem>
                            <SelectItem value="partition">Partition</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label className="text-xs font-medium uppercase tracking-[0.2em] text-slate-700">Style</Label>
                        <Input value={productForm.style} onChange={(e) => setProductForm({ ...productForm, style: e.target.value })} className="mt-2 rounded-sm" data-testid="product-style-input" />
                      </div>
                      <div>
                        <Label className="text-xs font-medium uppercase tracking-[0.2em] text-slate-700">Material</Label>
                        <Input value={productForm.material} onChange={(e) => setProductForm({ ...productForm, material: e.target.value })} className="mt-2 rounded-sm" data-testid="product-material-input" />
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label className="text-xs font-medium uppercase tracking-[0.2em] text-slate-700">Base Price ($)</Label>
                        <Input type="number" value={productForm.base_price} onChange={(e) => setProductForm({ ...productForm, base_price: parseFloat(e.target.value) || 0 })} className="mt-2 rounded-sm" data-testid="product-base-price-input" />
                      </div>
                      <div>
                        <Label className="text-xs font-medium uppercase tracking-[0.2em] text-slate-700">Price per sq ft ($)</Label>
                        <Input type="number" value={productForm.price_per_sqft} onChange={(e) => setProductForm({ ...productForm, price_per_sqft: parseFloat(e.target.value) || 0 })} className="mt-2 rounded-sm" data-testid="product-price-per-sqft-input" />
                      </div>
                    </div>
                    <div>
                      <Label className="text-xs font-medium uppercase tracking-[0.2em] text-slate-700">Description</Label>
                      <Input value={productForm.description} onChange={(e) => setProductForm({ ...productForm, description: e.target.value })} className="mt-2 rounded-sm" data-testid="product-description-input" />
                    </div>
                    <Button onClick={handleSaveProduct} className="w-full bg-[#0052FF] hover:bg-[#0043D1] rounded-sm" data-testid="save-product-button">
                      {editingProduct ? 'Update Product' : 'Create Product'}
                    </Button>
                  </div>
                </DialogContent>
              </Dialog>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {products.map((p) => (
                <Card key={p.id} className="border border-slate-200 rounded-sm shadow-none hover:-translate-y-0.5 transition-transform duration-150" data-testid={`product-card-${p.id}`}>
                  <CardHeader className="p-6">
                    <div className="flex items-center justify-between">
                      <Badge className="bg-slate-100 text-slate-700 rounded-sm">{p.category}</Badge>
                    </div>
                    <CardTitle className="text-lg font-medium mt-2">{p.name}</CardTitle>
                    <CardDescription>{p.style} | {p.material}</CardDescription>
                  </CardHeader>
                  <CardContent className="p-6 pt-0 space-y-3">
                    <p className="text-xs text-slate-500">{p.description}</p>
                    <div className="space-y-1 text-sm border-t border-slate-100 pt-3">
                      <div className="flex justify-between"><span className="text-slate-500">Base:</span><span className="font-mono font-medium">&#8377;{p.base_price}</span></div>
                      <div className="flex justify-between"><span className="text-slate-500">Per sq ft:</span><span className="font-mono font-medium">&#8377;{p.price_per_sqft}</span></div>
                    </div>
                    <div className="flex gap-2 pt-2">
                      <Button variant="outline" size="sm" onClick={() => { setEditingProduct(p); setProductForm(p); setProductDialogOpen(true); }} className="flex-1 rounded-sm" data-testid={`edit-product-${p.id}`}>
                        <Pencil size={14} className="mr-1" /> Edit
                      </Button>
                      <Button variant="outline" size="sm" onClick={() => handleDeleteProduct(p.id)} className="flex-1 rounded-sm text-red-600 hover:text-red-700 hover:border-red-200" data-testid={`delete-product-${p.id}`}>
                        <Trash2 size={14} className="mr-1" /> Delete
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </TabsContent>

          {/* Quotes Tab */}
          <TabsContent value="quotes" className="space-y-4">
            {quotes.length === 0 ? (
              <Card className="border-slate-200 rounded-sm shadow-none"><CardContent className="py-12 text-center"><p className="text-slate-500">No quotes yet</p></CardContent></Card>
            ) : quotes.map((q) => (
              <Card key={q.id} className="border border-slate-200 rounded-sm shadow-none" data-testid={`admin-quote-${q.id}`}>
                <CardHeader className="p-6">
                  <div className="flex items-start justify-between">
                    <div>
                      <CardTitle className="text-lg font-medium">{q.product?.name || 'Product'}</CardTitle>
                      <CardDescription>Customer: {q.customer_name} | {q.customer_email}</CardDescription>
                    </div>
                    <div className="flex items-center gap-2">
                      {getStatusBadge(q.status)}
                      <Select value={q.status} onValueChange={(v) => handleUpdateQuoteStatus(q.id, v)}>
                        <SelectTrigger className="w-32 rounded-sm" data-testid={`quote-status-select-${q.id}`}><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="pending">Pending</SelectItem>
                          <SelectItem value="ordered">Ordered</SelectItem>
                          <SelectItem value="confirmed">Confirmed</SelectItem>
                          <SelectItem value="completed">Completed</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="p-6 pt-0">
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                    <div><span className="text-xs uppercase tracking-[0.2em] text-slate-500">Dimensions</span><p className="font-mono font-medium mt-1">{q.width} x {q.height} ft</p></div>
                    <div><span className="text-xs uppercase tracking-[0.2em] text-slate-500">Qty</span><p className="font-mono font-medium mt-1">{q.quantity}</p></div>
                    <div><span className="text-xs uppercase tracking-[0.2em] text-slate-500">Area</span><p className="font-mono font-medium mt-1">{q.area.toFixed(2)} sq ft</p></div>
                    <div><span className="text-xs uppercase tracking-[0.2em] text-slate-500">Total</span><p className="text-[#0052FF] font-mono font-medium text-lg mt-1">&#8377;{q.total_price.toFixed(2)}</p></div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </TabsContent>

          {/* Orders Tab */}
          <TabsContent value="orders" className="space-y-4">
            {orders.length === 0 ? (
              <Card className="border-slate-200 rounded-sm shadow-none"><CardContent className="py-12 text-center"><p className="text-slate-500">No orders yet</p></CardContent></Card>
            ) : orders.map((o) => (
              <Card key={o.id} className="border border-slate-200 rounded-sm shadow-none" data-testid={`admin-order-${o.id}`}>
                <CardHeader className="p-6">
                  <div className="flex items-start justify-between">
                    <div>
                      <CardTitle className="text-lg font-medium">{o.quote?.product?.name || 'Product'}</CardTitle>
                      <CardDescription>Order: {o.id.slice(0, 8)}...</CardDescription>
                    </div>
                    <div className="flex items-center gap-2">
                      {getStatusBadge(o.status)}
                      <Select value={o.status} onValueChange={(v) => handleUpdateOrderStatus(o.id, v)}>
                        <SelectTrigger className="w-32 rounded-sm" data-testid={`order-status-select-${o.id}`}><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="pending">Pending</SelectItem>
                          <SelectItem value="confirmed">Confirmed</SelectItem>
                          <SelectItem value="in_production">In Production</SelectItem>
                          <SelectItem value="quality_check">Quality Check</SelectItem>
                          <SelectItem value="ready_for_delivery">Ready for Delivery</SelectItem>
                          <SelectItem value="out_for_delivery">Out for Delivery</SelectItem>
                          <SelectItem value="delivered">Delivered</SelectItem>
                          <SelectItem value="completed">Completed</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="p-6 pt-0">
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div><span className="text-xs uppercase tracking-[0.2em] text-slate-500">Delivery</span><p className="mt-1">{o.delivery_address}</p></div>
                    <div><span className="text-xs uppercase tracking-[0.2em] text-slate-500">Amount</span><p className="text-[#0052FF] font-mono font-medium text-lg mt-1">&#8377;{o.quote?.total_price?.toFixed(2)}</p></div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </TabsContent>

          {/* Inventory Tab */}
          <TabsContent value="inventory" className="space-y-4">
            <div className="flex justify-between items-center">
              <h3 className="text-2xl font-medium text-slate-900">Inventory</h3>
              <Dialog open={inventoryDialogOpen} onOpenChange={setInventoryDialogOpen}>
                <DialogTrigger asChild>
                  <Button className="bg-[#0052FF] hover:bg-[#0043D1] rounded-sm" data-testid="add-inventory-button"><Plus size={18} className="mr-2" /> Add Inventory</Button>
                </DialogTrigger>
                <DialogContent className="rounded-sm" data-testid="inventory-dialog">
                  <DialogHeader><DialogTitle>Add Inventory</DialogTitle></DialogHeader>
                  <div className="space-y-4 py-4">
                    <div>
                      <Label className="text-xs font-medium uppercase tracking-[0.2em] text-slate-700">Product</Label>
                      <Select value={inventoryForm.product_id} onValueChange={(v) => setInventoryForm({ ...inventoryForm, product_id: v })}>
                        <SelectTrigger className="mt-2 rounded-sm" data-testid="inventory-product-select"><SelectValue placeholder="Select product" /></SelectTrigger>
                        <SelectContent>{products.map((p) => (<SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>))}</SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-xs font-medium uppercase tracking-[0.2em] text-slate-700">Quantity</Label>
                      <Input type="number" value={inventoryForm.quantity} onChange={(e) => setInventoryForm({ ...inventoryForm, quantity: parseInt(e.target.value) || 0 })} className="mt-2 rounded-sm" data-testid="inventory-quantity-input" />
                    </div>
                    <div>
                      <Label className="text-xs font-medium uppercase tracking-[0.2em] text-slate-700">Location</Label>
                      <Input value={inventoryForm.location} onChange={(e) => setInventoryForm({ ...inventoryForm, location: e.target.value })} className="mt-2 rounded-sm" data-testid="inventory-location-input" />
                    </div>
                    <Button onClick={handleCreateInventory} className="w-full bg-[#0052FF] hover:bg-[#0043D1] rounded-sm" data-testid="save-inventory-button">Add to Inventory</Button>
                  </div>
                </DialogContent>
              </Dialog>
            </div>
            {inventory.length === 0 ? (
              <Card className="border-slate-200 rounded-sm shadow-none"><CardContent className="py-12 text-center"><p className="text-slate-500">No inventory items</p></CardContent></Card>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {inventory.map((item) => (
                  <Card key={item.id} className="border border-slate-200 rounded-sm shadow-none" data-testid={`inventory-card-${item.id}`}>
                    <CardHeader className="p-6">
                      <CardTitle className="text-lg font-medium">{item.product?.name || 'Product'}</CardTitle>
                      <CardDescription>{item.location}</CardDescription>
                    </CardHeader>
                    <CardContent className="p-6 pt-0">
                      <div className="flex justify-between text-sm">
                        <span className="text-slate-500">Quantity:</span>
                        <span className="font-mono font-medium text-lg">{item.quantity}</span>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Customers Tab */}
          <TabsContent value="customers" className="space-y-4">
            {customers.length === 0 ? (
              <Card className="border-slate-200 rounded-sm shadow-none"><CardContent className="py-12 text-center"><p className="text-slate-500">No customers yet</p></CardContent></Card>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {customers.map((c, i) => (
                  <Card key={i} className="border border-slate-200 rounded-sm shadow-none" data-testid={`customer-card-${i}`}>
                    <CardHeader className="p-6">
                      <CardTitle className="text-lg font-medium">{c.name}</CardTitle>
                      <CardDescription>{c.email}</CardDescription>
                    </CardHeader>
                    <CardContent className="p-6 pt-0 text-sm space-y-1">
                      {c.phone && <div><span className="text-slate-500">Phone:</span> <span>{c.phone}</span></div>}
                      <div><span className="text-slate-500">Joined:</span> <span>{new Date(c.created_at).toLocaleDateString()}</span></div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Analytics Tab */}
          <TabsContent value="analytics">
            <AnalyticsDashboard />
          </TabsContent>

          {/* Settings Tab */}
          <TabsContent value="settings" className="space-y-6">
            <Card className="border border-slate-200 rounded-sm shadow-none" data-testid="terms-editor">
              <CardHeader className="p-4 sm:p-6">
                <CardTitle className="text-xl font-medium">Terms & Conditions</CardTitle>
                <CardDescription className="text-sm text-slate-500">Edit the terms shown on PDF invoices. Each line will appear as a separate term.</CardDescription>
              </CardHeader>
              <CardContent className="p-4 sm:p-6 pt-0 space-y-4">
                <textarea
                  value={terms}
                  onChange={(e) => setTerms(e.target.value)}
                  rows={10}
                  className="w-full border border-slate-200 rounded-sm p-3 text-sm text-slate-700 font-mono resize-y focus:outline-none focus:ring-2 focus:ring-[#0052FF]/20 focus:border-[#0052FF]"
                  placeholder="1. Payment is due within 30 days...&#10;2. All products carry warranty..."
                  data-testid="terms-textarea"
                />
                <div className="flex items-center justify-between">
                  <p className="text-xs text-slate-400">{terms.split('\n').filter(l => l.trim()).length} terms</p>
                  <Button onClick={handleSaveTerms} disabled={termsSaving} className="bg-[#0052FF] hover:bg-[#0043D1] rounded-sm" data-testid="save-terms-button">
                    <Save size={16} className="mr-2" />
                    {termsSaving ? 'Saving...' : 'Save Terms'}
                  </Button>
                </div>
              </CardContent>
            </Card>

            <Card className="border border-slate-200 rounded-sm shadow-none">
              <CardHeader className="p-4 sm:p-6">
                <CardTitle className="text-xl font-medium">Company Information</CardTitle>
                <CardDescription className="text-sm text-slate-500">Displayed on invoices and emails</CardDescription>
              </CardHeader>
              <CardContent className="p-4 sm:p-6 pt-0">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
                  <div className="space-y-2">
                    <h4 className="font-medium text-slate-900">Factory</h4>
                    <p className="text-slate-600">60 Darussalam Check Mujgund</p>
                    <p className="text-slate-600">Srinagar, J&K 190017</p>
                    <p className="text-slate-600">Ph: 6006612592</p>
                  </div>
                  <div className="space-y-2">
                    <h4 className="font-medium text-slate-900">Office</h4>
                    <p className="text-slate-600">1st Floor, Darussalam Complex</p>
                    <p className="text-slate-600">Opp. Old General Bus Stand, Batamaloo</p>
                    <p className="text-slate-600">Srinagar, J&K 190009</p>
                    <p className="text-slate-600">Ph: 6006612593</p>
                  </div>
                  <div className="space-y-2">
                    <h4 className="font-medium text-slate-900">Bank Details</h4>
                    <p className="text-slate-600">HDFC Bank Ltd</p>
                    <p className="text-slate-600">A/C: 50200072321282</p>
                    <p className="text-slate-600">IFSC: HDFC0003580</p>
                  </div>
                  <div className="space-y-2">
                    <h4 className="font-medium text-slate-900">Tax</h4>
                    <p className="text-slate-600">GSTIN: 01BLOPS3940K1ZT</p>
                    <p className="text-slate-600">GST Rate: 18%</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}
